import {
    IonBackButton,
    IonButton,
    IonButtons,
    IonChip,
    IonContent,
    IonHeader,
    IonIcon,
    IonItem,
    IonLabel,
    IonList,
    IonNote,
    IonPage,
    IonSpinner,
    IonText,
    IonTitle,
    IonToolbar,
    useIonViewDidLeave,
    useIonViewWillEnter
} from "@ionic/react";

import {
    calendarOutline,
    trendingDownOutline,
    trendingUpOutline
} from "ionicons/icons";

import {
    useCallback,
    useEffect,
    useRef,
    useState
} from "react";

import { useParams } from "react-router-dom";

import "./FantasyProjections.css";

interface NextGame {
    gameId: string;
    date: string;
    week: string;
    opponentTeamId: string;
    isHome: boolean;
}

interface NextWeek {
    date: string;
    week: string;
    games: NextGame[];
}

interface RecommendedLineupPlayer {
    playerId: string;
    recommendedSlot: string;
    projection: number;
}

interface ProjectionPlayer {
    playerId: string;
    name: string;

    positionId: string;
    position: string;

    teamId: string;
    teamName: string;

    slot: string;

    recommendedSlot: string;
    isRecommendedStarter: boolean;

    projection: number;
    recentAverage: number;
    headToHeadAverage: number | null;

    opponentAdjustment: number;
    opponentPositionAverage: number | null;
    leaguePositionAverage: number | null;

    gamesUsed: number;

    confidence: "alta" | "media" | "baja";

    nextGame: NextGame | null;
}

interface FantasyProjectionResponse {
    success: boolean;

    leagueId: string;
    leagueName: string;

    index?: {
        generatedAt?: string;
        schemaVersion?: number;
        yearsBack?: number;
        playersIndexed?: number;
    };

    competition: {
        id: string;
        name: string;
        family: string;
    };

    nextWeek: NextWeek | null;

    projections: ProjectionPlayer[];

    recommendedLineup: RecommendedLineupPlayer[];
}



function formatDate(date?: string): string {
    if (!date) {
        return "Sin fecha";
    }

    const parsedDate = new Date(
        `${date}T12:00:00`
    );

    if (Number.isNaN(parsedDate.getTime())) {
        return date;
    }

    return new Intl.DateTimeFormat(
        "es-MX",
        {
            day: "numeric",
            month: "short",
            year: "numeric"
        }
    ).format(parsedDate);
}

function getConfidenceColor(
    confidence: ProjectionPlayer["confidence"]
): string {
    if (confidence === "alta") {
        return "success";
    }

    if (confidence === "media") {
        return "warning";
    }

    return "medium";
}
function formatIndexGeneratedAt(
    generatedAt?: string
): string {
    if (!generatedAt) {
        return "Índice histórico sin fecha disponible";
    }

    const generatedDate = new Date(generatedAt);

    if (Number.isNaN(generatedDate.getTime())) {
        return "Índice histórico sin fecha disponible";
    }

    return new Intl.DateTimeFormat(
        "es-MX",
        {
            dateStyle: "medium",
            timeStyle: "short"
        }
    ).format(generatedDate);
}

function getAdjustmentClass(
    adjustment: number
): string {
    if (adjustment > 0) {
        return "fantasy-projection-card__adjustment fantasy-projection-card__adjustment--positive";
    }

    if (adjustment < 0) {
        return "fantasy-projection-card__adjustment fantasy-projection-card__adjustment--negative";
    }

    return "fantasy-projection-card__adjustment";
}

const FantasyProjections: React.FC = function () {
    const { id } = useParams<{
        id: string;
    }>();

    const [
        data,
        setData
    ] = useState<FantasyProjectionResponse | null>(
        null
    );

    const [
        loading,
        setLoading
    ] = useState(true);

    const [
        error,
        setError
    ] = useState("");

    /*
    Conserva la respuesta actual sin volver inestable
    la función de consulta automática.
*/
const dataReference = useRef<FantasyProjectionResponse | null>(
    null
);

/*
    Evita solicitudes duplicadas si coincide el intervalo,
    el regreso a la pestaña y una carga inicial.
*/
const requestInProgressReference = useRef(false);

/*
    Identifica la carga vigente. Evita que una respuesta antigua
    sobrescriba datos de una liga que el usuario ya abandonó.
*/
const requestVersionReference = useRef(0);

/*
    Permite cancelar la petición HTTP que esté en curso cuando
    el usuario abandona esta vista o cambia de liga.
*/
const abortControllerReference = useRef<AbortController | null>(
    null
);

/*
    Ionic conserva páginas previas en memoria.
    Esta referencia evita actualizar Proyecciones mientras
    el usuario está navegando en otra pantalla.
*/
const isPageActiveReference = useRef(false);

    const loadProjections = useCallback(
        
    async function (initialLoad = false) {
    if (!id || requestInProgressReference.current) {
        return;
    }

    /*
        Conserva la versión de esta petición. Si el usuario cambia
        de liga antes de que responda el servidor, será descartada.
    */
    const requestVersion = requestVersionReference.current;

    const abortController = new AbortController();

abortControllerReference.current = abortController;

    requestInProgressReference.current = true;


        /*
            El spinner de pantalla completa sólo aparece
            al entrar por primera vez a la liga.
        */
        if (
            initialLoad ||
            dataReference.current === null
        ) {
            setLoading(true);
        }

        try {
            const response = await fetch(
                `${
                    process.env.PUBLIC_URL || ""
                }/control_FantasyProjections.php?id=${encodeURIComponent(
                    id
                )}&t=${Date.now()}`,
                {
                    credentials: "include",
                    cache: "no-store",
                    signal: abortController.signal
                }
            );

            const result = await response.json();

            /*
    La vista cambió de liga mientras se esperaba esta respuesta.
    No debe modificar los datos de la liga actual.
*/
if (
    requestVersion !== requestVersionReference.current
) {
    return;
}

            if (!response.ok || !result.success) {
                throw new Error(
                    result.message ||
                        "No fue posible obtener las proyecciones."
                );
            }

            dataReference.current = result;
            setData(result);
            setError("");
        } catch (requestError) {

            if (abortController.signal.aborted) {
        return;
    }
    /*
        No muestra errores de solicitudes pertenecientes a una liga
        anterior o a una vista que ya no está vigente.
    */
    if (
        requestVersion !== requestVersionReference.current
    ) {
        return;
    }

    const errorMessage =
        requestError instanceof Error
            ? requestError.message
            : "Ocurrió un error al cargar las proyecciones.";

    /*
        Conserva información previa si falló una actualización
        automática temporal.
    */
    if (dataReference.current === null) {
        setError(errorMessage);
    }

} finally {
    if (
        requestVersion === requestVersionReference.current &&
        abortControllerReference.current === abortController
    ) {
        requestInProgressReference.current = false;
        abortControllerReference.current = null;
        setLoading(false);
    }
}


    },
    [id]
);
/*
    Cuando el usuario entra o vuelve a esta vista,
    se consulta inmediatamente la información actual.
*/
useIonViewWillEnter(
    useCallback(
        function () {
            isPageActiveReference.current = true;

            loadProjections();
        },
        [loadProjections]
    )
);

/*
    Cuando el usuario sale a otra vista Ionic,
    se detienen las actualizaciones automáticas.
*/
useIonViewDidLeave(
    useCallback(function () {
        isPageActiveReference.current = false;

        /*
            Detiene la petición que ya no se necesita.
        */
        abortControllerReference.current?.abort();
        abortControllerReference.current = null;

        /*
            También invalida cualquier respuesta que hubiera llegado
            justo antes de que se completara la cancelación.
        */
        requestVersionReference.current += 1;
        requestInProgressReference.current = false;
    }, [])
);



/*
    Carga inicial al entrar a una liga o al cambiar de liga.
*/
useEffect(
    function () {
        abortControllerReference.current?.abort();
abortControllerReference.current = null;

        requestVersionReference.current += 1;

        dataReference.current = null;
        requestInProgressReference.current = false;

        setData(null);
        setError("");
        setLoading(true);

        loadProjections(true);
    },
    [id, loadProjections]
);

/*
    Consulta automática cada cinco minutos, pero solamente
    mientras esta vista Ionic esté activa y la aplicación visible.
*/
useEffect(
    function () {
        const intervalId = window.setInterval(
            function () {
                if (
                    isPageActiveReference.current &&
                    document.visibilityState === "visible"
                ) {
                    loadProjections();
                }
            },
            5 * 60 * 1000
        );

        return function () {
            window.clearInterval(intervalId);
        };
    },
    [loadProjections]
);

/*
    Al volver a la pestaña o aplicación, recupera datos recientes
    únicamente si el usuario sigue en esta pantalla.
*/
useEffect(
    function () {
        function handleVisibilityChange() {
            if (
                isPageActiveReference.current &&
                document.visibilityState === "visible"
            ) {
                loadProjections();
            }
        }

        document.addEventListener(
            "visibilitychange",
            handleVisibilityChange
        );

        return function () {
            document.removeEventListener(
                "visibilitychange",
                handleVisibilityChange
            );
        };
    },
    [loadProjections]
);


const orderedProjections = data
    ? [...data.projections].sort(
          function (left, right) {
              if (
                  left.isRecommendedStarter !==
                  right.isRecommendedStarter
              ) {
                  return left.isRecommendedStarter ? -1 : 1;
              }

              return right.projection - left.projection;
          }
      )
    : [];
const recommendedSlotOrder = [
    "QB",
    "RB",
    "WR",
    "FLEX",
    "K",
    "DEF"
];

const recommendedStarters = orderedProjections
    .filter(function (player) {
        return player.isRecommendedStarter;
    })
    .sort(function (left, right) {
        const leftSlotPosition =
            recommendedSlotOrder.indexOf(
                left.recommendedSlot
            );

        const rightSlotPosition =
            recommendedSlotOrder.indexOf(
                right.recommendedSlot
            );

        if (leftSlotPosition !== rightSlotPosition) {
            return leftSlotPosition - rightSlotPosition;
        }

        return right.projection - left.projection;
    });

const recommendedProjectionTotal =
    recommendedStarters.reduce(
        function (total, player) {
            return total + player.projection;
        },
        0
    );


    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonBackButton
                            defaultHref={`/fantasy/${id}`}
                        />
                    </IonButtons>

                    <IonTitle>
                        Proyecciones
                    </IonTitle>

                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen>
                {loading ? (
                    <section className="fantasy-projections-state">
                        <IonSpinner name="crescent" />

                        <p>
                            Calculando proyecciones Fantasy...
                        </p>
                    </section>
                ) : null}

                {!loading && error ? (
                    <section className="fantasy-projections-state">
                        <IonText color="danger">
                            <h2>
                                No fue posible cargar las proyecciones
                            </h2>

                            <p>{error}</p>
                        </IonText>

                        <IonButton
                            onClick={function () {
                                loadProjections();
                            }}
                        >
                            Reintentar
                        </IonButton>
                    </section>
                ) : null}

                {!loading && !error && data ? (
    <>
        <section className="fantasy-projections-hero">

                            <p className="fantasy-projections-hero__league">
                                {data.leagueName}
                            </p>

                            <h1>
                                {data.competition.name}
                            </h1>

                            {data.nextWeek ? (
                                <div className="fantasy-projections-hero__week">
                                    <IonIcon
                                        icon={calendarOutline}
                                    />

                                    <span>
                                        {data.nextWeek.week
                                            ? `Jornada ${data.nextWeek.week} · `
                                            : ""}

                                        {formatDate(
                                            data.nextWeek.date
                                        )}
                                    </span>
                                </div>
                            ) : (
                                <p className="fantasy-projections-hero__empty">
                                    No hay una jornada futura disponible.
                                </p>
                            )}
                        </section>

                        <section className="fantasy-projections-summary">
                            <div>
                                <strong>
                                    {data.projections.length}
                                </strong>

                                <span>
                                    Jugadores en roster
                                </span>
                            </div>

                            <div>
                                <strong>
                                    {
                                        data.projections.filter(
                                            function (player) {
                                                return (
                                                    player.nextGame !==
                                                    null
                                                );
                                            }
                                        ).length
                                    }
                                </strong>

                                <span>
                                    Con partido próximo
                                </span>
                            </div>
                        </section>
                        {recommendedStarters.length > 0 ? (
    <section className="fantasy-recommended-lineup">
        <div className="fantasy-recommended-lineup__header">
            <div>
                <p>
                    ALINEACIÓN RECOMENDADA
                </p>

                <h2>
                    Titulares sugeridos
                </h2>
            </div>

            <div className="fantasy-recommended-lineup__total">
                <strong>
                    {recommendedProjectionTotal.toFixed(1)}
                </strong>

                <span>PTS PROY.</span>
            </div>
        </div>

        <div className="fantasy-recommended-lineup__players">
            {recommendedStarters.map(
                function (player) {
                    return (
                        <div
                            className="fantasy-recommended-lineup__player"
                            key={player.playerId}
                        >
                            <span className="fantasy-recommended-lineup__slot">
                                {player.recommendedSlot}
                            </span>

                            <div className="fantasy-recommended-lineup__info">
                                <strong>
                                    {player.name}
                                </strong>

                                <span>
                                    {player.position} ·{" "}
                                    {player.teamName}
                                </span>
                            </div>

                            <strong className="fantasy-recommended-lineup__projection">
                                {player.projection.toFixed(1)}
                            </strong>
                        </div>
                    );
                }
            )}
        </div>

        <p className="fantasy-recommended-lineup__note">
            Recomendación calculada automáticamente según
            proyección, rival y rendimiento reciente.
        </p>
    </section>
) : null}

                        {data.projections.length === 0 ? (
                            <section className="fantasy-projections-state">
                                <h2>
                                    Tu roster está vacío
                                </h2>

                                <p>
                                    No hay jugadores seleccionados para
                                    calcular proyecciones.
                                </p>
                            </section>
                        ) : (
                            
                            <IonList className="fantasy-projections-list">
                                {orderedProjections.map(

                                    function (player) {
                                        const hasGame =
                                            player.nextGame !==
                                            null;

                                        const adjustmentLabel =
                                            player.opponentAdjustment > 0
                                                ? `+${player.opponentAdjustment.toFixed(
                                                      1
                                                  )}`
                                                : player.opponentAdjustment.toFixed(
                                                      1
                                                  );

                                        return (
                                            <IonItem
                                                key={player.playerId}
                                                className="fantasy-projection-card"
                                                lines="none"
                                            >
                                                <IonLabel>
                                                    <div className="fantasy-projection-card__top">
                                                        <div>
                                                            <div className="fantasy-projection-card__identity">
                                                                <IonChip
                                                                    color="primary"
                                                                    outline
                                                                >
                                                                    {
                                                                        player.position
                                                                    }
                                                                </IonChip>

                                                                {player.isRecommendedStarter ? (
                                                                    <IonChip color="success">
                                                                        {player.recommendedSlot}
                                                                    </IonChip>
                                                                ) : (
                                                                    <IonChip color="medium" outline>
                                                                        BN
                                                                    </IonChip>
                                                                )}


                                                                {player.slot ? (
                                                                    <IonChip
                                                                        color="medium"
                                                                        outline
                                                                    >
                                                                        {
                                                                            player.slot
                                                                        }
                                                                    </IonChip>
                                                                ) : null}
                                                            </div>

                                                            <h2>
                                                                {player.name}
                                                            </h2>

                                                            <p>
                                                                {player.teamName ||
                                                                    "Equipo no disponible"}
                                                            </p>
                                                        </div>

                                                        <div className="fantasy-projection-card__score">
                                                            <strong>
                                                                {hasGame
                                                                    ? player.projection.toFixed(
                                                                          1
                                                                      )
                                                                    : "—"}
                                                            </strong>

                                                            <span>
                                                                PTS PROY.
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {hasGame ? (
                                                        <>
                                                            <div className="fantasy-projection-card__game">
                                                                <span>
                                                                    {player.nextGame?.isHome
                                                                        ? "vs."
                                                                        : "@"}
                                                                </span>

                                                                <strong>
                                                                    {
                                                                        player
                                                                            .nextGame
                                                                            ?.opponentTeamId
                                                                    }
                                                                </strong>

                                                                <span>
                                                                    {formatDate(
                                                                        player
                                                                            .nextGame
                                                                            ?.date
                                                                    )}
                                                                </span>
                                                            </div>

                                                            <div className="fantasy-projection-card__metrics">
                                                                <div>
                                                                    <span>
                                                                        Promedio reciente
                                                                    </span>

                                                                    <strong>
                                                                        {player.recentAverage.toFixed(
                                                                            1
                                                                        )}
                                                                    </strong>
                                                                </div>

                                                                <div>
                                                                    <span>
                                                                        Vs. rival
                                                                    </span>

                                                                    <strong>
                                                                        {player.headToHeadAverage !==
                                                                        null
                                                                            ? player.headToHeadAverage.toFixed(
                                                                                  1
                                                                              )
                                                                            : "—"}
                                                                    </strong>
                                                                </div>

                                                                <div>
                                                                    <span>
                                                                        Ajuste rival
                                                                    </span>

                                                                    <strong
                                                                        className={getAdjustmentClass(
                                                                            player.opponentAdjustment
                                                                        )}
                                                                    >
                                                                        {player.opponentAdjustment >
                                                                        0 ? (
                                                                            <IonIcon
                                                                                icon={
                                                                                    trendingUpOutline
                                                                                }
                                                                            />
                                                                        ) : null}

                                                                        {player.opponentAdjustment <
                                                                        0 ? (
                                                                            <IonIcon
                                                                                icon={
                                                                                    trendingDownOutline
                                                                                }
                                                                            />
                                                                        ) : null}

                                                                        {
                                                                            adjustmentLabel
                                                                        }
                                                                    </strong>
                                                                </div>
                                                            </div>

                                                            <div className="fantasy-projection-card__footer">
                                                                <IonChip
                                                                    color={getConfidenceColor(
                                                                        player.confidence
                                                                    )}
                                                                    outline
                                                                >
                                                                    Confianza{" "}
                                                                    {
                                                                        player.confidence
                                                                    }
                                                                </IonChip>

                                                                <IonNote>
                                                                    {
                                                                        player.gamesUsed
                                                                    }{" "}
                                                                    juegos recientes
                                                                </IonNote>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <p className="fantasy-projection-card__no-game">
                                                            Este equipo no tiene
                                                            partido en la siguiente
                                                            jornada.
                                                        </p>
                                                    )}
                                                </IonLabel>
                                            </IonItem>
                                        );
                                    }
                                )}
                            </IonList>
                        )}
                    </>
                ) : null}
            </IonContent>
        </IonPage>
    );
};

export default FantasyProjections;
